import { useCallback, useContext } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";
import { useDetailsStore } from "@/stores/useDetailsStore";

import useAxiosPrivate from "../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../api/resources";
import { useAuth } from "../../context/Auth/AuthProvider";
import { AlertContext } from "../../context/alert/AlertContextProvider";
import { ALERT_MESSAGES } from "../../context/alert/types";
import { useDashboardContext } from "../../context/dashoard/DashboardProvider";
import { USER_PERMISSIONS } from "../../roles/types/enums";

import useResponse from "./useResponse";

const useRefreshDashboardData = () => {
  const { alertMessage } = useContext(AlertContext);
  const { dashboardWidgets } = useDashboardContext();
  const { getAccessStatus } = useAuth();

  const { handleCommonError } = useResponse();
  const { getData } = useAxiosPrivate();
  const { t } = useTranslation();

  const setResetDetailsSectionOnPageChange = useDetailsStore(
    state => state.setResetDetailsSection
  );

  const {
    newsData,
    setNewsData,
    setDriverDownloadsStatus,
    setVehicleDownloadsStatus,
    setDeviceStatus,
    setCompanyCards,
    setCCExpiry,
    setCredits,
    setIsDashboardLoading,
    setIsInitialLoading,
    setNewsError,
    setDriverDownloadsError,
    setVehicleDownloadsError,
    setDeviceStatusError,
    setCompanyCardsError,
    setCCExpiryError,
    setCreditsError
  } = useDashboardStore();

  const refreshNewsData = async () => {
    setNewsError(false);
    try {
      setIsDashboardLoading(true);
      const response = await getData(ApiResources.NewsItems);
      setNewsData(response.data);
    } catch (error) {
      handleCommonError(error, t);
      setNewsError(true);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  const refreshDashboardCardData = async (
    setData: (data: any) => void,
    resource: string,
    resetData: boolean = true,
    setError?: (hasError: boolean) => void
  ) => {
    setError?.(false);
    try {
      setIsDashboardLoading(true);
      // reset value to null to remove flash of old data
      resetData && setData(null);
      const response = await getData(resource);
      setData(response.data);
    } catch (error) {
      handleCommonError(error, t);
      setError?.(true);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  const refreshDashboardData = () => {
    dashboardWidgets.showNews &&
      refreshDashboardCardData(
        setNewsData,
        ApiResources.NewsItems,
        false,
        setNewsError
      );

    dashboardWidgets.showDeviceStatus &&
      refreshDashboardCardData(
        setDeviceStatus,
        ApiResources.DashboardDevices,
        undefined,
        setDeviceStatusError
      );

    dashboardWidgets.showCompanyCards &&
      refreshDashboardCardData(
        setCompanyCards,
        ApiResources.DashboardCompanyCards,
        undefined,
        setCompanyCardsError
      );

    dashboardWidgets.showDriverDownloadStatus &&
      refreshDashboardCardData(
        setDriverDownloadsStatus,
        ApiResources.DashboardDriverDownloads,
        undefined,
        setDriverDownloadsError
      );

    dashboardWidgets.showVehicleDownloadStatus &&
      refreshDashboardCardData(
        setVehicleDownloadsStatus,
        ApiResources.DashboardVehicleDownloads,
        undefined,
        setVehicleDownloadsError
      );

    // do not call cc expiry on old dashboard since it does not exist in a design
    dashboardWidgets.showCCExpiration &&
      refreshDashboardCardData(
        setCCExpiry,
        ApiResources.DashboardCompanyCardsExpirySummary,
        undefined,
        setCCExpiryError
      );

    getAccessStatus(USER_PERMISSIONS.SeeCreditsDashboard) &&
      dashboardWidgets.showCredits &&
      refreshDashboardCardData(
        setCredits,
        ApiResources.DashboardCredits,
        undefined,
        setCreditsError
      ); // do not call credits api if user is not allowed to see credits
  };

  const finishLoadingIfAllDataIsFetched = useCallback(() => {
    if (newsData || alertMessage?.title === ALERT_MESSAGES.actionFailed) {
      setIsInitialLoading(false);
      setResetDetailsSectionOnPageChange(false);
    }
  }, [
    newsData,
    setIsInitialLoading,
    setResetDetailsSectionOnPageChange,
    alertMessage
  ]);

  // the function below is used to reset the data when the page is changed,
  // so that the loading state is shown again
  const resetDashboardDataOnPageChange = useCallback(() => {
    // Use the reset function from the store
    useDashboardStore.getState().resetDashboardData();
  }, []);

  return {
    refreshDashboardData,
    resetDashboardDataOnPageChange,
    finishLoadingIfAllDataIsFetched,
    refreshNewsData
  };
};

export default useRefreshDashboardData;
